"use client";

import { useEffect, useRef, useState } from 'react';
import AppImage from '@/components/ui/AppImage';

export default function AppPreviewSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef?.current) {
      observer?.observe(sectionRef?.current);
    }

    return () => observer?.disconnect();
  }, []);

  const features = [
  {
    id: 'feature_profile',
    title: 'Smart Profiles',
    description: 'Detailed compatibility breakdown for each match',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_106dfd05d-1770331633840.png",
    alt: 'MeetX app profile screen showing detailed user compatibility metrics and personality insights'
  },
  {
    id: 'feature_chat',
    title: 'Meaningful Conversations',
    description: 'AI-powered conversation starters based on shared interests',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_125a6200a-1770331633846.png",
    alt: 'MeetX chat interface with AI-suggested conversation topics and icebreaker questions'
  },
  {
    id: 'feature_video',
    title: 'Video Dating',
    description: 'Safe, built-in video calls before meeting in person',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_163397eb7-1770331636091.png",
    alt: 'MeetX video call feature showing split screen with both users during virtual date'
  }];


  return (
    <section ref={sectionRef} className="py-24 px-6 bg-background overflow-hidden">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <h2
            className={`text-4xl md:text-6xl font-bold tracking-tight text-foreground mb-6 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`
            }>
            
            Experience the <span className="text-primary">App</span>
          </h2>
          <p
            className={`text-lg text-muted-foreground font-light transition-all duration-700 delay-100 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`
            }>
            
            Beautiful design meets powerful technology. Here&apos;s a peek at what makes MeetX different.
          </p>
        </div>

        {/* Device Mockup with Floating Cards */}
        <div className="relative max-w-5xl mx-auto">
          {/* Central Phone Mockup */}
          <div
            className={`relative z-10 max-w-sm mx-auto transition-all duration-1000 ${
            isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`
            }>
            
            <div className="relative">
              {/* Phone Frame */}
              <div className="relative bg-card rounded-[3rem] p-3 border-8 border-muted shadow-2xl">
                <div className="bg-background rounded-[2.5rem] overflow-hidden aspect-[9/19.5]">
                  <AppImage
                    src="https://img.rocket.new/generatedImages/rocket_gen_img_12603d17f-1770331635932.png"
                    alt="MeetX mobile app main screen showing curated daily matches with compatibility scores"
                    className="w-full h-full object-cover" />
                  
                </div>
                {/* Notch */}
                <div className="absolute top-6 left-1/2 -translate-x-1/2 w-32 h-7 bg-card rounded-full" />
              </div>
            </div>
          </div>

          {/* Floating Feature Cards */}
          {features?.map((feature, index) =>
          <div
            key={feature?.id}
            className={`absolute glass-card rounded-2xl p-4 transition-all duration-1000 hidden lg:block ${
            isVisible ?
            'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`
            }
            style={{
              transitionDelay: `${index * 200 + 300}ms`,
              top: index === 0 ? '10%' : index === 1 ? '45%' : '80%',
              left: index === 1 ? '5%' : 'auto',
              right: index === 0 || index === 2 ? '5%' : 'auto',
              width: '280px'
            }}>
            
              <div className="relative h-48 rounded-xl overflow-hidden mb-4">
                <AppImage
                src={feature?.image}
                alt={feature?.alt}
                className="w-full h-full object-cover" />
              
              </div>
              <h4 className="text-lg font-bold text-foreground mb-2">{feature?.title}</h4>
              <p className="text-sm text-muted-foreground">{feature?.description}</p>
            </div>
          )}

          {/* Decorative Elements */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full pointer-events-none">
            <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-primary/10 rounded-full blur-[100px]" />
            <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-secondary/10 rounded-full blur-[100px]" />
          </div>
        </div>
      </div>
    </section>);

}